<script setup lang="ts">
// ================================
// IMPORTS
// ================================
import { useDebounceFn, useClipboard, useEventBus } from "@vueuse/core"
import { computed, ref } from "vue"
import { useConfigStore } from "@core/stores/config.store"
import { useI18n } from "vue-i18n"
import { useRedirect } from "@core/composables"
import type { Address } from "@types"

const { t } = useI18n({
    messages: {
        fr: {
            tooltip: {
                copy: {
                    label: "Copier le numéro du compteur",
                    notification: "Numéro copié dans le presse-papier",
                },
                delete: "Supprimer la source",
                settings: "Accéder aux paramètres",
                data: "Accéder aux données",
                accessBuilding: "Accéder au bâtiment",
                sourceInfoGeneral:
                    "Accéder aux informations générales de la source",
            },
            consent: {
                tag: "Demande de consentement en cours",
                unnamed: "Sans nom",
            },
            meter: "Compteur",
            weather_station: "Station météo",
            sensor: "Capteur",
        },
    },
})

type PartialSource = {
    _id_source?: string
    _id?: string
    name?: string
    idsource?: string
    type?: string
    idtype?: string
    isUsed?: boolean
    /** Identifiant de l'opérateur (ex: orange, enedis, meteofrance) */
    operator?: string
    /** Marque et modèle (idnames) pour résolution de l'image du modèle */
    details?: { brand?: string; model?: string }
    /** true si la source n’est liée qu’à une demande de consentement en cours (liste V5) */
    consentRequestPending?: boolean
    /** Bâtiments rattachés (navigation depuis la demande) */
    buildings?: {
        _id: string
        name: string
        niveau?: string
        address: Address
    }[]
}

// ================================
// TYPES & INTERFACES
// ================================
interface Props {
    /**
     * Données de la source à afficher (capteur ou compteur)
     */
    source: PartialSource | null
    /**
     * Identifiant du bâtiment (pour navigation contexte compteurs)
     */
    buildingId?: string
    /**
     * Afficher le toggle d'activation (isUsed). Masqué pour les stations météo.
     */
    showToggleUsed?: boolean
    /**
     * Désactiver le toggle
     */
    disableToggle?: boolean
    /**
     * Afficher le bouton de suppression
     */
    showRemove?: boolean
    /**
     * Afficher les actions d'accès (data / settings / delete)
     */
    showActions?: boolean
    /**
     * Désactiver l'image du modèle
     */
    disableModelImage?: boolean
    /**
     * Désactiver l'image de la marque
     */
    disableBrandImage?: boolean
}

interface Emits {
    (e: "click:data", payload: { id: string }): void
    (e: "click:settings", payload: { id: string; type: string }): void
    (e: "update:activate", payload: { value: boolean; sourceId: string }): void
    (e: "click:delete", payload: { id: string }): void
}

// ================================
// PROPS & EMITS
// ================================
const props = withDefaults(defineProps<Props>(), {
    buildingId: "",
    showToggleUsed: true,
    disableToggle: false,
    showRemove: false,
    showActions: true,
})
const emit = defineEmits<Emits>()
const configStore = useConfigStore()
const { redirectWrapper } = useRedirect()

const buildingListDialogVisible = ref(false)

// ================================
// DONNÉES CALCULÉES
// ================================
/**
 * Type d'icône pour BaseSourceIcon (type ou idtype selon la source)
 */
const getSourceIconType = computed(() => {
    const s = props.source
    if (!s) return "default"
    const t = s.idtype ?? s.type ?? ""
    return typeof t === "string" ? t : "default"
})

/**
 * Libellé affiché : nom ou idsource
 */
const getIdSource = computed(() => {
    const s = props.source
    if (!s) return ""
    const idsource = s.idsource ?? s._id_source ?? ""
    return `N° ${idsource}`
})

/**
 * Nom de la source avec préfixe " - " (pour affichage après un libellé type "Station météo")
 */
const getName = computed(() => {
    const s = props.source
    if (!s?.name) return ""
    return `- ${s.name}`
})

/**
 * Nom brut de la source (pour affichage seul, ex. liste des sources)
 */
const getSourceName = computed(() => props.source?.name ?? "")

/**
 * Image et nom de l'opérateur de la source (pour affichage du logo)
 */
const getBrandImage = computed(() => {
    const operatorId = props.source?.operator
    if (!operatorId) return { img: "", name: "" }
    const operator = configStore.getOperatorById(operatorId)
    return {
        img: operator?.img ?? "",
        name: operator?.name ?? "",
    }
})

/**
 * Image et nom du modèle de la source (depuis sourceModels).
 * Le parent (ex. PageBuildingSensorList) appelle fetchSourceModels pour charger
 * la liste ; on résout le modèle via getSourceModelById(details.model).
 */
const getModelImage = computed(() => {
    const modelId = props.source?.details?.model
    if (!modelId) return { img: "", name: "" }
    const model = configStore.getSourceModelById(modelId)
    return {
        img: model?.img ?? "",
        name: model?.name ?? "",
    }
})

/**
 * Source affichée uniquement dans le cadre d’une demande de consentement en cours
 */
const getIsConsentRequestPending = computed(
    () => props.source?.consentRequestPending === true
)

/**
 * Bâtiments associés, format attendu par DialogMeterBuildingList
 */
const getBuildingsForDialog = computed(() => {
    const list = props.source?.buildings ?? []
    return list.map((b) => ({
        _id: b._id,
        name: b.name || t("consent.unnamed"),
        address: b.address,
        niveau: b.niveau ?? "",
    }))
})

const getHasBuildingsForConsent = computed(
    () => getBuildingsForDialog.value.length > 0
)

/**
 * Tooltip du tag consentement si aucun bâtiment (redirection vers info générale)
 */
const getConsentTagTooltip = computed(() =>
    getHasBuildingsForConsent.value ? undefined : t("tooltip.sourceInfoGeneral")
)

// ================================
// INTERACTIONS
// ================================
/**
 * Gère le clic sur le bouton de données
 */
function onClickData(): void {
    const id = props.source?._id_source ?? props.source?._id ?? ""
    if (id) emit("click:data", { id })
}

/**
 * Gère le clic sur le bouton de suppression
 */
function onClickDelete(): void {
    const id = props.source?._id_source ?? props.source?._id ?? ""
    if (id) emit("click:delete", { id })
}

/**
 * Gère le clic sur le bouton de paramètres
 */
function onClickSettings(): void {
    const s = props.source
    const id = s?._id_source ?? s?._id ?? ""
    const type = s?.type ?? s?.idtype ?? ""
    if (id) emit("click:settings", { id, type })
}

/**
 * Gère le changement de statut de la source (isUsed).
 * Debounced pour éviter d’émettre trop rapidement sur des toggles successifs.
 */
const onToggleActivate = useDebounceFn((value: boolean): void => {
    const id = props.source?._id_source ?? props.source?._id ?? ""
    if (id) emit("update:activate", { value, sourceId: id })
}, 400)

const bus = useEventBus("notification")
const { copy } = useClipboard()

/**
 * Copie le numéro du compteur (idsource) dans le presse-papier et affiche une notification.
 */
function onClickCopyIdSource(): void {
    const idsource = props.source?.idsource ?? props.source?._id_source ?? ""
    copy(idsource)
    bus.emit({
        severity: "success",
        title: t("tooltip.copy.notification"),
        life: 3000,
    })
}

/**
 * Navigue vers le bâtiment (compteur), choix si plusieurs, sinon page info générale de la source
 */
function onNavigateToBuildingForConsent(): void {
    const list = getBuildingsForDialog.value
    const sourceId = props.source?._id_source ?? props.source?._id ?? ""
    if (list.length === 0) {
        if (sourceId) {
            redirectWrapper("/source/:_id_source/info-general", {
                _id_source: sourceId,
            })
        }
        return
    }
    if (list.length === 1) {
        redirectWrapper("/building/:_id_building/compteur", {
            _id_building: list[0]._id,
        })
        return
    }
    buildingListDialogVisible.value = true
}
</script>

<template>
    <div
        class="item-source card p-4 flex justify-between flex-wrap gap-3 bg-white hover:border-primary-200 transition-all duration-300"
    >
        <!-- En-tête : icône + infos + toggle -->
        <div
            class="item-source__header flex items-center gap-3 md:flex-row flex-col w-full md:w-auto"
        >
            <!-- Toggle d'activation de la source (stop pour ne pas déclencher le clic parent) -->
            <div v-if="showToggleUsed" @click.stop>
                <BaseToggleSwitch
                    :model-value="props.source?.isUsed ?? false"
                    :disabled="disableToggle"
                    input-id="item-source-toggle"
                    aria-label="Activer ou désactiver la source"
                    @update:model-value="onToggleActivate"
                />
            </div>
            <!-- Icône de la source -->
            <BaseSourceIcon :type="getSourceIconType" :size="48" />
            <!-- Informations de la source -->
            <div
                class="item-source__info flex-1 w-full md:w-[clamp(0px,32rem,40rem)]"
            >
                <div class="flex items-center gap-2">
                    <p class="font-medium truncate">{{ getIdSource }}</p>
                    <BaseButton
                        v-tooltip.top="t('tooltip.copy.label')"
                        variant="text"
                        rounded
                        aria-label="Copier le numéro du compteur"
                        @click.stop="onClickCopyIdSource"
                        class="p-1 w-8 h-8 shrink-0"
                    >
                        <BaseIcon icon="pi pi-copy" />
                    </BaseButton>
                </div>
                <div class="flex items-center gap-2 text-sm text-gray-500">
                    <p v-if="props.source?.type === 'meteo'">
                        {{ t("weather_station") }}{{ getName }}
                    </p>
                    <p v-else-if="getSourceName" class="truncate">
                        {{ getSourceName }}
                    </p>
                </div>
                <slot
                    name="info"
                    v-bind="{
                        model: getModelImage.name,
                        brand: getBrandImage.name,
                        type: props.source?.type,
                        _id_source: props.source?._id_source,
                    }"
                ></slot>
            </div>
            <!-- Image et nom du modèle de la source (depuis sourceModels) -->
            <div class="flex items-center gap-4 flex-wrap">
                <div
                    v-if="getBrandImage.img && !disableBrandImage"
                    class="item-source__brand flex items-center shrink-0"
                >
                    <img
                        :src="getBrandImage.img"
                        :alt="getBrandImage.name"
                        class="max-h-12 max-w-16 object-contain"
                    />
                </div>
                <div
                    v-if="getModelImage.img && !disableModelImage"
                    class="item-source__brand flex items-center shrink-0"
                    v-tooltip.top="getModelImage.name"
                >
                    <img
                        :src="getModelImage.img"
                        :alt="getModelImage.name"
                        class="max-h-12 max-w-16 object-contain"
                    />
                </div>
            </div>
        </div>
        <!-- Actions (demande de consentement en cours) -->
        <div
            v-if="getIsConsentRequestPending"
            class="item-source__actions flex flex-wrap items-center justify-end gap-2"
        >
            <BaseTag
                severity="info"
                class="cursor-pointer"
                v-tooltip.top="getConsentTagTooltip"
                @click.stop="onNavigateToBuildingForConsent"
            >
                <span>{{ t("consent.tag") }}</span>
            </BaseTag>
            <BaseButton
                v-if="getHasBuildingsForConsent"
                severity="secondary"
                text
                rounded
                icon="pi pi-cog"
                v-tooltip.top="t('tooltip.accessBuilding')"
                :aria-label="t('tooltip.accessBuilding')"
                @click.stop="onNavigateToBuildingForConsent"
            />
            <BaseButton
                v-else
                severity="secondary"
                text
                rounded
                icon="pi pi-cog"
                v-tooltip.top="t('tooltip.settings')"
                :aria-label="t('tooltip.settings')"
                @click.stop="onClickSettings"
            />
        </div>
        <!-- Actions (liste classique) -->
        <div
            v-else-if="showActions"
            class="item-source__actions flex flex-wrap gap-2"
        >
            <BaseButton
                severity="primary"
                text
                rounded
                icon="pi pi-chart-line"
                v-tooltip.top="t('tooltip.data')"
                @click.stop="onClickData"
            />
            <BaseButton
                severity="secondary"
                text
                rounded
                icon="pi pi-cog"
                v-tooltip.top="t('tooltip.settings')"
                @click.stop="onClickSettings"
            />
            <BaseButton
                v-if="showRemove"
                severity="danger"
                text
                rounded
                icon="pi pi-trash"
                v-tooltip.top="t('tooltip.delete')"
                @click.stop="onClickDelete"
            />
        </div>
        <DialogMeterBuildingList
            v-if="getBuildingsForDialog.length > 1"
            v-model:visible="buildingListDialogVisible"
            :buildings="getBuildingsForDialog"
            target="compteur"
        />
    </div>
</template>
